/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useMemo, useState } from 'react';

import { type Header, type Table, type TableOptions } from '@tanstack/react-table';

import {
	cssMap,
	cx,
	jsx,
	type StrictXCSSProp,
	type XCSSAllProperties,
	type XCSSAllPseudos,
} from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';

import { type CharlieTableProps, type TableType } from '../../charlie-table.types';
import { isDropAllowed } from '../../utils/drag-and-drop.utils';
import { isReservedColumn } from '../../utils/reserved-column.utils';
import { DEFAULT_COLUMN_ACTIONS, type TableActions } from '../more-actions/more-actions-menu';
import { type ColumnMetadata, columnMoveOptions } from '../more-actions/more-actions.utils';

import { HeadCell } from './head-cell';
import { HeaderCellContent } from './header-cell-content';
import { HeaderTooltip } from './header-tooltip';
import { handleHeaderMouseMove } from './header.utils';
import { PragmaticHeadCell } from './pragmatic-head-cell';

const styles = cssMap({
	borderTopLeft: {
		borderTopLeftRadius: token('radius.small'),
		borderInlineStart: `${token('border.width')} solid ${token('color.border')}`,
	},
	borderTopRight: {
		borderTopRightRadius: token('radius.small'),
		borderInlineEnd: `${token('border.width')} solid ${token('color.border')}`,
	},
	borderTopRightT26: {
		borderTopRightRadius: token('radius.xlarge'),
	},
	borderTopLeftT26: {
		borderTopLeftRadius: token('radius.xlarge'),
	},
	PinnedColumn: {
		position: 'sticky',
		zIndex: 300,
	},
	UnpinnedColumn: {
		position: 'relative',
	},
	ColumnPadding: {
		paddingInlineStart: token('space.100'),
	},
	ColumnReorder: {
		paddingInlineStart: token('space.0'),
		cursor: 'grab',
	},
	columnResponsive: {
		flexGrow: 1,
	},
});

declare module '@tanstack/table-core' {
	//@ts-expect-error suggested tanstack solution
	interface ColumnMeta {
		description?: ReactNode;
	}
}

interface CharlieTableHeaderProps<T extends object> {
	tableType?: TableType;
	enableResizing: boolean;
	columnResizeMode?: CharlieTableProps<T>['columnResizeMode'];
	charlieTableColumn: Header<T, unknown>;
	index: number;
	charlieTableState: Table<T>;
	enableColumnReorder?: boolean;
	hasWidths: boolean;
	labels: NonNullable<CharlieTableProps<T>['labels']>;
	xcss?: StrictXCSSProp<XCSSAllProperties, XCSSAllPseudos>;
	analyticsContext?: Record<string, any>;
	interactionName?: string;
	enableColumnActions?: CharlieTableProps<T>['enableColumnActions'];
	enableRowVirtualization: boolean;
	responsiveColumnSizing: CharlieTableProps<T>['responsiveColumnSizing'];
	columnActions?: CharlieTableProps<T>['columnActions'];
	onColumnSizingChangeRef?: TableOptions<T>['onColumnSizingChange'];
	onResizeHandleHoverChange?: (columnId: string | false) => void;
}

/**
 * The e2e header used by the CharlieTable, intended for usage within the e2e CharlieTable component.
 */
export const CharlieTableHeader = <T extends object>({
	labels,
	tableType,
	charlieTableColumn,
	charlieTableState,
	index,
	enableResizing = false,
	columnResizeMode = 'onChange',
	enableColumnReorder,
	hasWidths,
	xcss,
	analyticsContext,
	interactionName,
	enableColumnActions,
	enableRowVirtualization,
	responsiveColumnSizing,
	columnActions,
	onColumnSizingChangeRef,
	onResizeHandleHoverChange,
}: CharlieTableHeaderProps<T>) => {
	// Evaluate enableColumnActions - supports boolean, config object, or function
	const evaluatedColumnActions = useMemo(() => {
		if (typeof enableColumnActions === 'function') {
			// Function: call it with the column to get boolean or config
			return enableColumnActions(charlieTableColumn.column);
		}
		// Direct value: boolean or config object
		return enableColumnActions;
	}, [enableColumnActions, charlieTableColumn.column]);

	// Determine if column actions are enabled and merge configurations
	const { isEnabled, config } = useMemo(() => {
		// If evaluatedColumnActions is false or undefined, disable
		if (evaluatedColumnActions === false || evaluatedColumnActions === undefined) {
			return { isEnabled: false, config: DEFAULT_COLUMN_ACTIONS };
		}

		// If evaluatedColumnActions is true, enable with merged config
		if (evaluatedColumnActions === true) {
			return {
				isEnabled: true,
				config: { ...DEFAULT_COLUMN_ACTIONS, ...columnActions },
			};
		}

		// If evaluatedColumnActions is a config object, enable with that config
		// Still merge with deprecated columnActions for backward compatibility
		return {
			isEnabled: true,
			config: { ...DEFAULT_COLUMN_ACTIONS, ...columnActions, ...evaluatedColumnActions },
		};
	}, [evaluatedColumnActions, columnActions]);

	const mergedColumnActions = config;

	const isResponsive = responsiveColumnSizing?.(charlieTableColumn.column);
	const [isHoveringCell, setHoveringCell] = useState<boolean>(false);
	const [isSortButtonFocused, setSortButtonFocused] = useState<boolean>(false);
	const [isMoreActionsButtonFocused, setIsMoreActionsButtonFocused] = useState<boolean>(false);
	const [isDragHandlerFocused] = useState<boolean>(false);
	const isPinned = charlieTableColumn.column.getIsPinned();

	const columnMetadata: ColumnMetadata = useMemo(
		() => ({
			id: charlieTableColumn.id,
			canSort: charlieTableColumn.column.getCanSort(),
			canHide: charlieTableColumn.column.getCanHide(),
			isSorted: charlieTableColumn.column.getIsSorted(),
			isPinned: charlieTableColumn.column.getIsPinned(),
			index: charlieTableColumn.column.getIndex(),
			isFirstColumn: (position) => charlieTableColumn.column.getIsFirstColumn(position),
			isLastColumn: (position) => charlieTableColumn.column.getIsLastColumn(position),
		}),
		[charlieTableColumn],
	);

	const allowedColumnActionMoves = useMemo(
		() => columnMoveOptions({ column: columnMetadata, tableState: charlieTableState.getState() }),
		[columnMetadata, charlieTableState],
	);

	const tableActions: TableActions = useMemo(
		() => ({
			toggleSorting: (desc: boolean) => {
				charlieTableColumn.column.toggleSorting(desc);
			},
			clearSorting: () => {
				charlieTableColumn.column.clearSorting();
			},
			setColumnOrder: (order: string[]) => {
				charlieTableState.setColumnOrder(order);
			},
			setColumnPinning: (pinning: { left?: string[]; right?: string[] }) => {
				charlieTableState.setColumnPinning(pinning);
			},
			setColumnVisibility: (visibility: Record<string, boolean>) => {
				charlieTableState.setColumnVisibility(visibility);
			},
			getState: () => charlieTableState.getState(),
		}),
		[charlieTableColumn, charlieTableState],
	);

	const isReserved = isReservedColumn(charlieTableColumn.id);

	// TODO: We probably don't need to be dependent on the enableColumnReorder prop here as it's more for dnd. But for now, we'll use it.
	const canColumnMove = useMemo(
		() =>
			charlieTableState.getState().columnOrder.length > 0
				? !!allowedColumnActionMoves.size
				: enableColumnReorder && !isPinned,
		[charlieTableState, allowedColumnActionMoves, enableColumnReorder, isPinned],
	);

	const canColumnReorderDnd = useMemo(
		() => enableColumnReorder && canColumnMove && !isReserved,
		[enableColumnReorder, canColumnMove, isReserved],
	);

	const [isMoreActionsOpened, setMoreActionsOpened] = useState<boolean>(false);

	const Header = useMemo(() => {
		if ((enableResizing || canColumnReorderDnd) && !isReservedColumn(charlieTableColumn.id)) {
			return PragmaticHeadCell;
		}
		return HeadCell;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enableResizing, canColumnMove]);

	const sortDirection = charlieTableColumn.column.getIsSorted();

	const isLastLeftPinnedColumn =
		charlieTableColumn.column.getIsPinned() === 'left' &&
		charlieTableColumn.column.getIsLastColumn('left');
	const isFirstRightPinnedColumn =
		charlieTableColumn.column.getIsPinned() === 'right' &&
		charlieTableColumn.column.getIsFirstColumn('right');

	const showSortButton = isHoveringCell || isSortButtonFocused || isMoreActionsOpened;

	const showDragHandler = (isHoveringCell || isDragHandlerFocused) && !isReserved;
	const showMoreActionsButton =
		(isHoveringCell || isMoreActionsButtonFocused || isMoreActionsOpened) && !isReserved;
	const showColumnActions =
		isHoveringCell ||
		showSortButton ||
		showMoreActionsButton ||
		charlieTableColumn.column.getIsSorted();
	const canColumnSort = charlieTableColumn.column.getCanSort();
	const canMoveColumns = allowedColumnActionMoves.size > 0;
	const isPinnedColumn = charlieTableColumn.column.getIsPinned();
	const isRightPinned = charlieTableState
		.getState()
		.columnPinning?.right?.includes(charlieTableColumn.id);

	const canHideColumn = mergedColumnActions.hiding;

	const canPinColumn = mergedColumnActions.pinToLeft && !isRightPinned;
	const canMoveToFront = mergedColumnActions.moveToFront && canMoveColumns && !isPinnedColumn;
	const canMoveToBack = mergedColumnActions.moveToBack && canMoveColumns && !isPinnedColumn;

	const isMoreActionsEnabled =
		isEnabled &&
		(canColumnSort ||
			canMoveColumns ||
			canPinColumn ||
			canMoveToFront ||
			canMoveToBack ||
			canHideColumn);

	const { maxSize, minSize } = charlieTableColumn.column.columnDef;

	let isPinningEdge: 'left' | 'right' | undefined;
	if (isLastLeftPinnedColumn) {
		isPinningEdge = 'left';
	} else if (isFirstRightPinnedColumn) {
		isPinningEdge = 'right';
	} else {
		isPinningEdge = undefined;
	}

	const shapeThemeRefresh = fg('charlie-table-shape-refresh');

	return (
		<Header
			isOverridingResponsiveColumnSizing={responsiveColumnSizing != null}
			tableType={tableType}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={{
				width: charlieTableColumn.getSize(),
				left: `var(--col-${charlieTableColumn.id}-left)`,
				right: `var(--col-${charlieTableColumn.id}-right)`,
				borderInlineEndWidth: `var(--col-${charlieTableColumn.id}-resizing-border-width, var(--col-${charlieTableColumn.id}-hover-border-width, ${
					shapeThemeRefresh && index === charlieTableState.getVisibleLeafColumns().length - 1
						? '0px'
						: tableType === 'INPUT' ||
							  index === charlieTableState.getVisibleLeafColumns().length - 1
							? token('border.width')
							: '0px'
				}))`,
				borderInlineEndColor: `var(--col-${charlieTableColumn.id}-resizing-border-color, var(--col-${charlieTableColumn.id}-hover-border-color, ${token('color.border')}))`,
				paddingInlineStart: canColumnReorderDnd || isReserved ? '0px' : undefined,
			}}
			xcss={cx(
				isResponsive && styles.columnResponsive,
				isPinned ? styles.PinnedColumn : styles.UnpinnedColumn,
				canColumnReorderDnd && styles.ColumnReorder,

				// When shape-theme-default, wrapper has radius and top border; no corner or top border on header cells
				index === 0 && !shapeThemeRefresh && styles.borderTopLeft,
				index === charlieTableState.getVisibleLeafColumns().length - 1 &&
					!shapeThemeRefresh &&
					styles.borderTopRight,
				index === 0 && !shapeThemeRefresh && fg('platform-dst-shape-theme-default')
					? styles.borderTopLeftT26
					: null,
				index === charlieTableState.getVisibleLeafColumns().length - 1
					? !shapeThemeRefresh && styles.borderTopRight
					: null,
				index === charlieTableState.getVisibleLeafColumns().length - 1 &&
					!shapeThemeRefresh &&
					fg('platform-dst-shape-theme-default')
					? styles.borderTopRightT26
					: null,
				index && index === 0 && !canColumnReorderDnd ? styles.ColumnPadding : null,
				xcss,
			)}
			isPinningEdge={isPinningEdge}
			onColumnResizeDrag={(width: number) => {
				if (columnResizeMode === 'onChange' || columnResizeMode === 'onChangeEnd') {
					charlieTableState.setColumnSizing((old) => ({
						...old,
						[charlieTableColumn.id]: width,
					}));
				}
				charlieTableState.setColumnSizingInfo((old) => ({
					...old,
					isResizingColumn: charlieTableColumn.id,
				}));
				// for onChange mode, call external callback on every drag
				if (columnResizeMode === 'onChange' && onColumnSizingChangeRef) {
					const newSizing = {
						...charlieTableState.getState().columnSizing,
						[charlieTableColumn.id]: width,
					};
					onColumnSizingChangeRef(newSizing);
				}
			}}
			onColumnResizeEnd={(finalWidth?: number) => {
				if (columnResizeMode === 'onEnd' && finalWidth !== undefined) {
					charlieTableState.setColumnSizing((old) => ({
						...old,
						[charlieTableColumn.id]: finalWidth,
					}));
				}
				// for onEnd and onChangeEnd, call external callback at the end
				if (
					(columnResizeMode === 'onEnd' || columnResizeMode === 'onChangeEnd') &&
					onColumnSizingChangeRef
				) {
					const currentSizing = charlieTableState.getState().columnSizing;
					// for onEnd, include the finalWidth since state was not updated yet
					const finalSizing =
						columnResizeMode === 'onEnd' && finalWidth !== undefined
							? { ...currentSizing, [charlieTableColumn.id]: finalWidth }
							: currentSizing;
					onColumnSizingChangeRef(finalSizing);
				}
				charlieTableState.setColumnSizingInfo((old) => ({
					...old,
					isResizingColumn: false,
				}));
				onResizeHandleHoverChange?.(false);
			}}
			{...(sortDirection && {
				sortDirection: sortDirection === 'asc' ? 'ascending' : 'descending',
			})}
			enableColumnReorder={canColumnReorderDnd}
			enableResizing={enableResizing}
			columnResizeMode={columnResizeMode}
			isDropAllowed={({ sourceColumnId, targetColumnId }) =>
				isDropAllowed({
					sourceColumnId,
					targetColumnId,
					columnPinning: charlieTableState.getState().columnPinning,
				})
			}
			width={
				hasWidths && (enableRowVirtualization || !charlieTableColumn.column.getIsLastColumn())
					? charlieTableColumn.getSize()
					: undefined
			}
			size={{
				min: minSize,
				max: maxSize,
				value: charlieTableColumn.getSize(),
			}}
			key={charlieTableColumn.id}
			id={charlieTableColumn.id}
			onMouseMove={(e) => handleHeaderMouseMove(e, isHoveringCell, setHoveringCell)}
			onMouseLeave={() => {
				if (isHoveringCell) {
					setHoveringCell(false);
				}
			}}
			resizeColumnLabel={
				typeof labels.resizeColumn === 'function'
					? labels.resizeColumn(charlieTableColumn)
					: labels.resizeColumn
			}
			onResizeHandleHoverChange={enableResizing ? onResizeHandleHoverChange : undefined}
		>
			<HeaderTooltip charlieTableColumn={charlieTableColumn}>
				{(tooltipProps) => (
					<HeaderCellContent
						charlieTableColumn={charlieTableColumn}
						charlieTableState={charlieTableState}
						labels={labels}
						columnMetadata={columnMetadata}
						tableActions={tableActions}
						allowedColumnActionMoves={allowedColumnActionMoves}
						mergedColumnActions={mergedColumnActions}
						canColumnReorderDnd={!!canColumnReorderDnd}
						canColumnSort={!!canColumnSort}
						isMoreActionsEnabled={!!isMoreActionsEnabled}
						showDragHandler={showDragHandler}
						showSortButton={showSortButton}
						sortDirection={sortDirection}
						showColumnActions={!!showColumnActions}
						showMoreActionsButton={!!showMoreActionsButton}
						isMoreActionsOpened={!!isMoreActionsOpened}
						onToggleMoreActions={() => setMoreActionsOpened((prev) => !prev)}
						onMoreActionsClose={() => {
							setMoreActionsOpened(false);
							setHoveringCell(false);
						}}
						onSortButtonFocusChange={setSortButtonFocused}
						onMoreActionsFocusChange={setIsMoreActionsButtonFocused}
						interactionName={interactionName}
						analyticsContext={analyticsContext}
						tooltipProps={tooltipProps}
					/>
				)}
			</HeaderTooltip>
		</Header>
	);
};
